import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Footer } from "@/components/Footer";

export default function Orders() {
  const { user, ready } = useAuth();
  const [orders, setOrders] = useState([]);
  const nav = useNavigate();
  const { add, setOpen } = useCart();

  useEffect(() => {
    if (ready && !user) nav("/auth", { state: { from: "/orders" } });
  }, [ready, user, nav]);

  useEffect(() => {
    if (user) api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  const reorder = async (order) => {
    try {
      const { data: products } = await api.get("/products");
      const map = {};
      products.forEach((p) => p.variants.forEach((v) => { map[v.lookup_key] = { p, v }; }));
      let added = 0, skipped = 0;
      order.items.forEach((it) => {
        const m = map[it.lookup_key];
        if (m && m.v.in_stock !== false) {
          add({ lookup_key: it.lookup_key, name: m.p.name, size: m.v.size, price: m.v.price, image_url: m.p.image_url, quantity: it.quantity });
          added += 1;
        } else { skipped += 1; }
      });
      if (added) { toast.success(`Added ${added} item${added > 1 ? "s" : ""} to your order`); setOpen(true); }
      if (skipped) toast(`${skipped} item${skipped > 1 ? "s" : ""} unavailable and skipped`);
      if (!added && !skipped) toast.error("Nothing to reorder");
    } catch { toast.error("Couldn't reorder right now"); }
  };

  return (
    <div className="App bg-[#080808] pt-16 min-h-screen flex flex-col">
      <div className="max-w-[1200px] mx-auto w-full px-6 md:px-10 py-16 flex-1">
        <p className="overline mb-4">Account</p>
        <h1 className="font-display font-light text-5xl md:text-6xl tracking-tighter mb-12">Order history.</h1>

        {orders.length === 0 ? (
          <div className="border border-[#222] p-12 text-center">
            <p className="text-[#8A8782] text-sm mb-6">No orders yet.</p>
            <Link to="/shop" className="btn-toxic px-8 py-3 inline-block">Start shopping</Link>
          </div>
        ) : (
          <div className="border border-[#222]" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="border-b border-[#1a1a1a] p-6 grid md:grid-cols-5 gap-4 items-center">
                <div>
                  <p className="overline">Order</p>
                  <p className="text-xs mt-1">#{o.id.slice(-8)}</p>
                </div>
                <div>
                  <p className="overline">Date</p>
                  <p className="text-xs mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="overline">Items</p>
                  <p className="text-xs mt-1">{o.items.reduce((s, i) => s + i.quantity, 0)} units</p>
                </div>
                <div>
                  <p className="text-lg">${o.amount.toFixed(2)}</p>
                  <span className="overline text-[#C7F000]">{o.fulfillment}</span>
                </div>
                <div className="md:text-right">
                  <button data-testid={`reorder-${o.id}`} onClick={() => reorder(o)}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#C7F000] text-[#C7F000] overline hover:bg-[#C7F000]/10 transition-colors">
                    <RotateCcw size={13} /> Reorder
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
